import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, Download, Video, Loader2, Check, RefreshCw, Bot, Brain, X as XIcon, Copy, Instagram, Hash, MessageSquare, Lock, Unlock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VentLogo from './VentLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviewTomorrowPage = () => {
  const navigate = useNavigate();
  const [agentData, setAgentData] = useState(null);
  const [igContent, setIgContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [igRegenerating, setIgRegenerating] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoPolling, setVideoPolling] = useState(false);
  const [sageImage, setSageImage] = useState(null);
  const [copied, setCopied] = useState('');
  const [lockStatus, setLockStatus] = useState(null);
  const [locking, setLocking] = useState(false);

  const fetchLockStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/topic/tomorrow/lock-status`);
      setLockStatus(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [agentRes, sageRes, igRes] = await Promise.all([
        axios.get(`${API}/agent/status`),
        axios.get(`${API}/sage/image`),
        axios.get(`${API}/agent/instagram`)
      ]);

      if (agentRes.data.status === 'not_run') {
        await axios.post(`${API}/agent/run`);
        const statusRes = await axios.get(`${API}/agent/status`);
        setAgentData(statusRes.data);
      } else {
        setAgentData(agentRes.data);
      }

      if (igRes.data.status === 'ready') {
        setIgContent(igRes.data);
      }

      if (sageRes.data.image_base64) {
        setSageImage(`data:image/png;base64,${sageRes.data.image_base64}`);
      }
    } catch (err) {
      toast.error('Failed to load preview');
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:preview]', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); fetchLockStatus(); }, [fetchData, fetchLockStatus]);

  const handleLock = async () => {
    if (lockStatus?.locked) {
      // Unlock flow with confirmation
      if (!window.confirm('Unlock tomorrow? This allows the topic and assets to be regenerated. Once posted to socials, you may end up out of sync again.')) return;
      try {
        setLocking(true);
        await axios.post(`${API}/topic/tomorrow/unlock`);
        await fetchLockStatus();
        toast.success('Tomorrow unlocked');
      } catch { toast.error('Unlock failed'); }
      finally { setLocking(false); }
      return;
    }

    try {
      setLocking(true);
      const res = await axios.post(`${API}/topic/tomorrow/lock`);
      toast.success('🔒 Locked! Assets are being prepared.');
      // Refresh everything
      await Promise.all([fetchData(), fetchLockStatus()]);
      // Begin polling video if it just kicked off
      if (res.data.video_state === 'generating' && res.data.video_id) {
        setVideoStatus({ status: 'generating', video_id: res.data.video_id });
        setVideoPolling(true);
        pollVideoStatus(res.data.video_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lock failed');
    } finally {
      setLocking(false);
    }
  };

  // Check video status on mount
  useEffect(() => {
    if (!agentData || agentData.status !== 'ready') return;
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    const videoId = `sage-${tomorrow}`;
    axios.get(`${API}/topic/video/${videoId}/status`)
      .then(res => {
        if (res.data.status === 'ready' || res.data.status === 'generating') {
          setVideoStatus({ ...res.data, video_id: videoId });
          if (res.data.status === 'generating') {
            setVideoPolling(true);
            pollVideoStatus(videoId);
          }
        }
      })
      .catch(() => {});
  }, [agentData]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const result = await axios.post(`${API}/agent/regenerate`);
      const statusRes = await axios.get(`${API}/agent/status`);
      setAgentData(statusRes.data);
      setVideoStatus(null);
      if (result.data.instagram) setIgContent(result.data.instagram);
      toast.success('Agent picked a new topic!');
    } catch (err) {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const handleIgRegenerate = async () => {
    setIgRegenerating(true);
    try {
      const res = await axios.post(`${API}/agent/instagram/regenerate`);
      if (res.data.status === 'success') {
        setIgContent(res.data);
        toast.success('New caption generated!');
      }
    } catch { toast.error('Failed to regenerate caption'); }
    finally { setIgRegenerating(false); }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(''), 2000);
    } catch { toast.error('Failed to copy'); }
  };

  const generateVideo = async () => {
    try {
      const res = await axios.post(`${API}/topic/tomorrow/video`);
      setVideoStatus(res.data);
      if (res.data.status === 'generating') {
        setVideoPolling(true);
        pollVideoStatus(res.data.video_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start video generation');
    }
  };

  const pollVideoStatus = (videoId) => {
    let pollCount = 0;
    const MAX_POLLS = 200; // 200 polls × 5s = 16+ min safety cap
    const interval = setInterval(async () => {
      pollCount += 1;
      try {
        const res = await axios.get(`${API}/topic/video/${videoId}/status`);
        setVideoStatus({ ...res.data, video_id: videoId });
        if (res.data.status === 'ready' || res.data.status === 'failed') {
          clearInterval(interval);
          setVideoPolling(false);
          if (res.data.status === 'ready') toast.success('Sage video is ready!');
          if (res.data.status === 'failed') toast.error(`Video generation failed${res.data.error ? ': ' + res.data.error : ''}`);
        } else if (pollCount >= MAX_POLLS) {
          clearInterval(interval);
          setVideoPolling(false);
          toast.error('Video generation timed out. Please try again.');
        }
      } catch {
        clearInterval(interval);
        setVideoPolling(false);
      }
    }, 5000);
  };

  const downloadVideo = () => {
    if (videoStatus?.video_id) {
      window.open(`${API}/topic/video/${videoStatus.video_id}/download`, '_blank');
    }
  };

  const tomorrowDate = new Date(Date.now() + 86400000).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-vent-bg flex items-center justify-center">
        <div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="w-12 h-12 border-2 border-vent-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-zinc-500 text-sm font-heading tracking-wider">AGENT IS SCANNING TRENDS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern">
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          <VentLogo size="small" />
        </div>
      </nav>

      <main className="pt-24 pb-32 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Bot className="w-5 h-5 text-vent-secondary" />
              <span className="text-vent-secondary text-xs font-heading font-bold tracking-[0.3em] uppercase">Topic Discovery Agent</span>
              <Bot className="w-5 h-5 text-vent-secondary" />
            </div>
            <p className="text-zinc-500 text-sm">{tomorrowDate}</p>
          </motion.div>

          {/* Agent Reasoning Card */}
          {agentData?.agent_generated && agentData?.reasoning && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass rounded-2xl p-4 mb-4 border border-vent-secondary/10">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-vent-secondary" />
                <span className="text-vent-secondary text-xs font-heading font-bold tracking-wider">AGENT'S REASONING</span>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">{agentData.reasoning}</p>
            </motion.div>
          )}

          {/* Selected Topic Card */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="glass rounded-3xl p-6 sm:p-8 mb-4 border border-vent-primary/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-vent-primary/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-vent-secondary/5 rounded-full blur-3xl" />

            {/* Sage */}
            <div className="flex justify-center mb-6">
              <motion.div animate={{ y: [0, -12, 0], rotate: [0, 3, -3, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} className="w-28 h-28 relative">
                <div className="absolute inset-0 bg-vent-primary/30 rounded-full blur-2xl animate-pulse" />
                {sageImage ? (
                  <img src={sageImage} alt="Sage" className="relative w-full h-full object-cover rounded-full" />
                ) : (
                  <div className="relative w-full h-full rounded-full bg-gradient-to-br from-vent-primary/30 to-vent-secondary/30 flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-vent-primary" />
                  </div>
                )}
              </motion.div>
            </div>

            <p className="text-zinc-400 text-sm text-center mb-6 italic">{agentData?.sage_message}</p>

            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white text-center mb-3" data-testid="tomorrow-topic-title">
              {agentData?.title}
            </h2>
            <p className="text-zinc-400 text-center mb-6" data-testid="tomorrow-topic-description">
              {agentData?.description}
            </p>

            <div className="text-center border-t border-white/5 pt-4">
              <p className="text-vent-secondary text-xs font-heading tracking-[0.2em]">VENT.TODAY</p>
              <p className="text-zinc-600 text-xs mt-1">Every human deserves to be heard</p>
            </div>
          </motion.div>

          {/* Candidates the agent considered */}
          {agentData?.candidates && agentData.candidates.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="glass rounded-2xl p-4 mb-6 border border-white/5">
              <h3 className="text-xs font-heading font-bold text-zinc-500 tracking-wider mb-3">OTHER CANDIDATES CONSIDERED</h3>
              <div className="space-y-2">
                {agentData.candidates.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
                    <span className="text-zinc-600 text-xs font-bold mt-0.5">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 text-sm">{c.title}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">{c.why_trending}</p>
                      {agentData.rejected_reasons && agentData.rejected_reasons[i] && (
                        <p className="text-red-400/60 text-xs mt-0.5 flex items-center gap-1">
                          <XIcon className="w-3 h-3" /> {agentData.rejected_reasons[i]}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 text-xs text-zinc-600 shrink-0">
                      <span title="Debate potential">{c.debate_potential}/10</span>
                      <span title="Gen Z relevance">{c.gen_z_relevance}/10</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* LOCK & PUBLISH — atomic one-button workflow */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              className={`rounded-2xl p-4 border ${lockStatus?.locked ? 'bg-vent-secondary/10 border-vent-secondary/30' : 'bg-vent-primary/5 border-vent-primary/20'}`}
              data-testid="lock-panel"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {lockStatus?.locked ? <Lock className="w-4 h-4 text-vent-secondary" /> : <Unlock className="w-4 h-4 text-vent-primary" />}
                  <span className={`text-xs font-heading font-bold tracking-widest uppercase ${lockStatus?.locked ? 'text-vent-secondary' : 'text-vent-primary'}`}>
                    {lockStatus?.locked ? 'Locked & Published-Ready' : 'Publish Lock'}
                  </span>
                </div>
                {lockStatus?.locked && (
                  <span className="text-[10px] text-zinc-500 tracking-wider">SCHEDULER PAUSED</span>
                )}
              </div>

              {/* Asset readiness checklist */}
              <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                <div className="flex flex-col items-center gap-1 py-2 rounded-lg bg-black/20" data-testid="asset-topic">
                  {lockStatus?.topic_ready ? <Check className="w-4 h-4 text-vent-secondary" /> : <Loader2 className="w-4 h-4 text-zinc-600 animate-spin" />}
                  <span className="text-zinc-500">Topic</span>
                </div>
                <div className="flex flex-col items-center gap-1 py-2 rounded-lg bg-black/20" data-testid="asset-ig">
                  {lockStatus?.instagram_ready ? <Check className="w-4 h-4 text-vent-secondary" /> : <span className="w-4 h-4 rounded-full border border-zinc-600" />}
                  <span className="text-zinc-500">Caption</span>
                </div>
                <div className="flex flex-col items-center gap-1 py-2 rounded-lg bg-black/20" data-testid="asset-video">
                  {lockStatus?.video_state === 'ready' ? (
                    <Check className="w-4 h-4 text-vent-secondary" />
                  ) : lockStatus?.video_state === 'generating' ? (
                    <Loader2 className="w-4 h-4 text-vent-primary animate-spin" />
                  ) : (
                    <span className="w-4 h-4 rounded-full border border-zinc-600" />
                  )}
                  <span className="text-zinc-500">Video</span>
                </div>
              </div>

              <button
                onClick={handleLock}
                disabled={locking || !lockStatus?.topic_ready}
                className={`w-full py-3.5 rounded-xl font-heading font-bold tracking-wider text-sm flex items-center justify-center gap-2 transition-all ${
                  lockStatus?.locked
                    ? 'bg-white/5 text-zinc-400 hover:bg-white/10 border border-white/10'
                    : 'bg-vent-secondary text-black hover:shadow-glow-green'
                }`}
                data-testid="lock-publish-btn"
              >
                {locking ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {lockStatus?.locked ? 'UNLOCKING...' : 'LOCKING & PREPARING ASSETS...'}</>
                ) : lockStatus?.locked ? (
                  <><Unlock className="w-4 h-4" /> UNLOCK (ADMIN OVERRIDE)</>
                ) : (
                  <><Lock className="w-4 h-4" /> LOCK & PUBLISH TOMORROW</>
                )}
              </button>

              {!lockStatus?.locked && (
                <p className="text-zinc-600 text-[10px] mt-2 text-center leading-relaxed">
                  Locks the topic, auto-generates IG caption + Sora video, and pauses the agent so nothing drifts.
                </p>
              )}
              {lockStatus?.locked && lockStatus?.video_state === 'generating' && (
                <p className="text-vent-primary text-[11px] mt-2 text-center flex items-center justify-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Sora 2 is rendering (5-7 min)... You can leave & come back.
                </p>
              )}
            </motion.div>

            {/* Regenerate */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              onClick={async () => {
                if (lockStatus?.locked && !window.confirm('Tomorrow is LOCKED. Regenerating will break sync with already-published assets. Continue?')) return;
                if (lockStatus?.locked) {
                  // Force unlock first
                  try { await axios.post(`${API}/topic/tomorrow/unlock`); await fetchLockStatus(); } catch { /* ignore */ }
                }
                handleRegenerate();
              }}
              disabled={regenerating}
              className={`w-full py-3.5 rounded-2xl font-heading font-bold tracking-wider text-sm flex items-center justify-center gap-3 border transition-all ${
                lockStatus?.locked
                  ? 'bg-amber-500/5 text-amber-400/80 hover:bg-amber-500/10 border-amber-500/20'
                  : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10 border-white/10'
              }`}
              data-testid="regenerate-btn"
            >
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : lockStatus?.locked ? <AlertTriangle className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
              {regenerating ? 'AGENT IS RE-SCANNING...' : 'REGENERATE (HUMAN OVERRIDE)'}
            </motion.button>

            {/* Generate Video */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              onClick={generateVideo}
              disabled={videoPolling || videoStatus?.status === 'ready'}
              className={`w-full py-4 rounded-2xl font-heading font-bold tracking-wider text-sm flex items-center justify-center gap-3 transition-all ${
                videoStatus?.status === 'ready'
                  ? 'bg-vent-secondary/20 text-vent-secondary border border-vent-secondary/30'
                  : videoPolling
                  ? 'bg-zinc-800 text-zinc-400 cursor-wait'
                  : 'bg-vent-primary text-white hover:shadow-glow-purple'
              }`}
              data-testid="generate-video-btn"
            >
              {videoStatus?.status === 'ready' ? (
                <><Check className="w-5 h-5" /> VIDEO READY</>
              ) : videoPolling ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> GENERATING SAGE VIDEO...</>
              ) : (
                <><Video className="w-5 h-5" /> GENERATE SAGE VIDEO (SORA 2)</>
              )}
            </motion.button>

            {videoStatus?.status === 'ready' && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={downloadVideo}
                className="w-full py-4 rounded-2xl font-heading font-bold tracking-wider text-sm bg-vent-secondary text-black flex items-center justify-center gap-3 hover:shadow-glow-green"
                data-testid="download-video-btn"
              >
                <Download className="w-5 h-5" /> DOWNLOAD FOR INSTAGRAM
              </motion.button>
            )}
          </div>

          {/* Instagram Content Agent */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 glass rounded-2xl p-5 border border-pink-500/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-bold text-zinc-300 tracking-wider flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-400" /> INSTAGRAM CONTENT
              </h3>
              <button
                onClick={igContent ? handleIgRegenerate : async () => { setIgRegenerating(true); try { const r = await axios.post(`${API}/agent/instagram/regenerate`); if (r.data.status === 'success') { setIgContent(r.data); toast.success('Caption generated!'); } } catch { toast.error('Failed'); } finally { setIgRegenerating(false); } }}
                disabled={igRegenerating}
                className="text-xs text-zinc-500 hover:text-pink-400 flex items-center gap-1 transition-colors"
                data-testid="ig-regenerate-btn"
              >
                {igRegenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {igContent ? 'Regenerate' : 'Generate'}
              </button>
            </div>

            {igContent ? (
              <div className="space-y-4">
                {/* Hook */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-zinc-600 text-xs font-heading tracking-wider">HOOK (FIRST LINE)</span>
                    <button onClick={() => copyToClipboard(igContent.hook, 'Hook')} className={`text-xs flex items-center gap-1 transition-colors ${copied === 'Hook' ? 'text-vent-secondary' : 'text-zinc-600 hover:text-white'}`}>
                      <Copy className="w-3 h-3" /> {copied === 'Hook' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-white text-sm font-semibold bg-black/30 rounded-xl p-3">{igContent.hook}</p>
                </div>

                {/* Full Caption */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-zinc-600 text-xs font-heading tracking-wider">CAPTION</span>
                    <button onClick={() => copyToClipboard(igContent.caption, 'Caption')} className={`text-xs flex items-center gap-1 transition-colors ${copied === 'Caption' ? 'text-vent-secondary' : 'text-zinc-600 hover:text-white'}`}>
                      <Copy className="w-3 h-3" /> {copied === 'Caption' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-zinc-300 text-sm bg-black/30 rounded-xl p-3 whitespace-pre-line">{igContent.caption}</p>
                </div>

                {/* Hashtags */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-zinc-600 text-xs font-heading tracking-wider flex items-center gap-1"><Hash className="w-3 h-3" /> HASHTAGS</span>
                    <button onClick={() => copyToClipboard(igContent.hashtags, 'Hashtags')} className={`text-xs flex items-center gap-1 transition-colors ${copied === 'Hashtags' ? 'text-vent-secondary' : 'text-zinc-600 hover:text-white'}`}>
                      <Copy className="w-3 h-3" /> {copied === 'Hashtags' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-pink-400/70 text-xs bg-black/30 rounded-xl p-3 break-words">{igContent.hashtags}</p>
                </div>

                {/* Story Text */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-zinc-600 text-xs font-heading tracking-wider flex items-center gap-1"><MessageSquare className="w-3 h-3" /> STORY OVERLAY TEXT</span>
                    <button onClick={() => copyToClipboard(igContent.story_text, 'Story')} className={`text-xs flex items-center gap-1 transition-colors ${copied === 'Story' ? 'text-vent-secondary' : 'text-zinc-600 hover:text-white'}`}>
                      <Copy className="w-3 h-3" /> {copied === 'Story' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-white text-sm font-medium bg-black/30 rounded-xl p-3">{igContent.story_text}</p>
                </div>

                {/* Copy All */}
                <button
                  onClick={() => copyToClipboard(`${igContent.caption}\n\n${igContent.hashtags}`, 'All')}
                  className={`w-full py-3 rounded-xl text-sm font-heading font-bold tracking-wider flex items-center justify-center gap-2 transition-all ${copied === 'All' ? 'bg-vent-secondary/20 text-vent-secondary' : 'bg-pink-500/10 text-pink-400 hover:bg-pink-500/20 border border-pink-500/20'}`}
                  data-testid="copy-all-ig"
                >
                  <Copy className="w-4 h-4" /> {copied === 'All' ? 'COPIED!' : 'COPY CAPTION + HASHTAGS'}
                </button>
              </div>
            ) : (
              <p className="text-zinc-600 text-sm text-center py-6">No content generated yet. Click Generate above or wait for the daily agent run.</p>
            )}
          </motion.div>

          {/* Agent Info */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 glass rounded-2xl p-5">
            <h3 className="font-heading text-sm font-bold text-zinc-300 mb-3 tracking-wider flex items-center gap-2">
              <Bot className="w-4 h-4 text-vent-secondary" /> HOW THE AGENT WORKS
            </h3>
            <ol className="space-y-2 text-zinc-500 text-sm">
              <li className="flex gap-2"><span className="text-vent-secondary font-bold">1.</span> Scans trending topics across social media, news & web</li>
              <li className="flex gap-2"><span className="text-vent-secondary font-bold">2.</span> Scores candidates by debate potential & Gen Z relevance</li>
              <li className="flex gap-2"><span className="text-vent-secondary font-bold">3.</span> Cross-checks against past 30 topics (no repeats)</li>
              <li className="flex gap-2"><span className="text-vent-secondary font-bold">4.</span> Selects the best one and generates topic content</li>
              <li className="flex gap-2"><span className="text-vent-secondary font-bold">5.</span> Runs daily at 8 PM UTC automatically</li>
            </ol>
            <p className="text-zinc-600 text-xs mt-3 italic">You can override the agent's choice anytime with the Regenerate button.</p>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default PreviewTomorrowPage;
